import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { AuthGuard } from './auth.guard';

@Injectable({
  providedIn: 'root'
})
export class CanUseNusoAdminGuard extends AuthGuard {
  /**
   * Nuso internal admin: an admin/superuser who is not a NECAM user. Used to
   * gate internal-only tooling (e.g. the customer Orders page) so it stays
   * hidden from customers, partners, and resellers.
   */
  isNusoAdmin(): Observable<boolean> {
    return this.loginService.completedLoginStatus()
      .pipe(
        map(status => {
          const session = status.loginSession;
          if (!status.isLoggedIn || !session || !session.roles) {
            return false;
          }
          // Mirrors the getSessionInfo `isNusoAdmin` selector: an admin or
          // superuser whose username is not a NECAM account.
          const isAdmin = session.roles.includes('admin') || session.roles.includes('superuser');
          return isAdmin && (session.username || '').indexOf('necam') === -1;
        })
      );
  }

  override canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    // First check authentication via the parent AuthGuard (handles its own
    // redirect to /login when not logged in).
    return super.canActivate(route, state).pipe(
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          return of(false);
        }
        return this.isNusoAdmin().pipe(
          tap(isNusoAdmin => {
            if (!isNusoAdmin) {
              this.router.navigate(['/']);
            }
          })
        );
      })
    );
  }
}
